import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type Context,
  type ReactNode,
} from "react";
import {
  PrintPreviewDialog,
  type PrintPreviewTarget,
} from "@/modules/print/PrintPreviewDialog";
import {
  EbrPrintPreviewDialog,
  type EbrPrintPreviewTarget,
} from "@/modules/print/EbrPrintPreviewDialog";

type PrintPreviewContextValue = {
  openPrintPreview: (target: PrintPreviewTarget) => void;
  openEbrPrintPreview: (target: EbrPrintPreviewTarget | string) => void;
  closePrintPreview: () => void;
};

const CONTEXT_KEY = "__galaxy_print_preview_context__";

/**
 * Keep a single Context identity across Vite HMR — otherwise Provider and
 * consumers can briefly (or permanently) bind different context objects and
 * `usePrintPreview` throws even though AppProviders still wraps the tree.
 */
function getPrintPreviewContext(): Context<PrintPreviewContextValue | null> {
  const g = globalThis as typeof globalThis & {
    [CONTEXT_KEY]?: Context<PrintPreviewContextValue | null>;
  };
  if (!g[CONTEXT_KEY]) {
    g[CONTEXT_KEY] = createContext<PrintPreviewContextValue | null>(null);
  }
  return g[CONTEXT_KEY];
}

const PrintPreviewContext = getPrintPreviewContext();

export function PrintPreviewProvider({ children }: { children: ReactNode }) {
  const [docTarget, setDocTarget] = useState<PrintPreviewTarget | null>(null);
  const [ebrTarget, setEbrTarget] = useState<EbrPrintPreviewTarget | null>(null);

  const openPrintPreview = useCallback((next: PrintPreviewTarget) => {
    setEbrTarget(null);
    setDocTarget(next);
  }, []);

  const openEbrPrintPreview = useCallback(
    (next: EbrPrintPreviewTarget | string) => {
      setDocTarget(null);
      setEbrTarget(typeof next === "string" ? { batchCode: next } : next);
    },
    [],
  );

  const closePrintPreview = useCallback(() => {
    setDocTarget(null);
    setEbrTarget(null);
  }, []);

  const value = useMemo(
    () => ({ openPrintPreview, openEbrPrintPreview, closePrintPreview }),
    [openPrintPreview, openEbrPrintPreview, closePrintPreview],
  );

  return (
    <PrintPreviewContext.Provider value={value}>
      {children}
      <PrintPreviewDialog
        open={Boolean(docTarget)}
        onOpenChange={(next) => {
          if (!next) closePrintPreview();
        }}
        target={docTarget}
      />
      <EbrPrintPreviewDialog
        open={Boolean(ebrTarget)}
        onOpenChange={(next) => {
          if (!next) closePrintPreview();
        }}
        target={ebrTarget}
      />
    </PrintPreviewContext.Provider>
  );
}

export function usePrintPreview(): PrintPreviewContextValue {
  const ctx = useContext(PrintPreviewContext);
  if (!ctx) {
    throw new Error("usePrintPreview must be used within PrintPreviewProvider");
  }
  return ctx;
}
